import React, { useEffect, useRef, useState } from 'react';

export interface SelectOption {
    value: string;
    label: string;
    /** Optional custom render for this option row in the dropdown */
    render?: (option: SelectOption) => React.ReactNode;
    style?: React.CSSProperties;
    className?: string;
}

interface SelectComponentProps {
    value: string;
    onChange: (value: string) => void;
    options: SelectOption[];
    placeholder?: string;
    style?: React.CSSProperties;
    className?: string;
    height?: string | number;
}

export default function SelectComponent({
    value,
    onChange,
    options,
    placeholder = 'Select…',
    style,
    className,
    height = '2rem',
}: SelectComponentProps) {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const selected = options.find(o => o.value === value);

    // Close on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Close on Escape
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, []);

    return (
        <div
            ref={containerRef}
            className={`sc-select ${className ?? ''}`}
            style={{ position: 'relative', display: 'inline-block', width: '100%', maxWidth: 220, ...style }}
        >
            {/* Trigger */}
            <div
                className="sc-select__trigger"
                onClick={() => setOpen(o => !o)}
                style={{ height }}
                aria-haspopup="listbox"
                aria-expanded={open}
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(o => !o); } }}
            >
                <span className="sc-select__value">
                    {selected ? (selected.render ? selected.render(selected) : selected.label) : placeholder}
                </span>
                <span className="sc-select__arrow" aria-hidden>▼</span>
            </div>

            {/* Dropdown */}
            {open && (
                <ul className="sc-select__dropdown" role="listbox">
                    {options.map(opt => (
                        <li
                            key={opt.value}
                            role="option"
                            aria-selected={opt.value === value}
                            className={`sc-select__option ${opt.value === value ? 'sc-select__option--selected' : ''} ${opt.className ?? ''}`}
                            style={opt.style}
                            onClick={() => { onChange(opt.value); setOpen(false); }}
                        >
                            {opt.render ? opt.render(opt) : opt.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
